import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeatureConfigService, FeatureConfigs } from '../../../services/feature-config.service';
import { FeatureTypes } from '../../../interfaces/feature-config.interface';
import { AssetPropertiesAvailability, FeatureConfigValues } from '../models/asset.model';

@Injectable({
  providedIn: 'root',
})
export class AssetPropertiesAvailabilityService {
  private readonly featureConfigService: FeatureConfigService = inject(FeatureConfigService);

  /**
   * Fetches feature configs from the API and returns asset properties data.
   * This method calls the API to get the feature configuration data.
   *
   * @returns Observable of AssetPropertiesAvailability object with all properties as arrays
   */
  public getAvailablePropertiesFromApi(): Observable<AssetPropertiesAvailability> {
    return this.featureConfigService.getFeatureConfigs([FeatureTypes.FEA_CAMPAIGN_ASSETLIST]).pipe(
      map((res: FeatureConfigs<FeatureTypes.FEA_CAMPAIGN_ASSETLIST>) => {
        const config: FeatureConfigValues | null =
          (res?.[FeatureTypes.FEA_CAMPAIGN_ASSETLIST] as FeatureConfigValues | null) ?? null;
        return this.getAvailableProperties(config);
      })
    );
  }

  /**
   * Returns an object with all asset properties as arrays.
   * Each property contains the actual data array, or an empty array if not available.
   *
   * @param config - The feature config values object (can be null or undefined)
   * @returns An object with all asset properties as arrays
   */
  public getAvailableProperties(config: FeatureConfigValues | null | undefined): AssetPropertiesAvailability {
    if (!config) {
      return this.getDefaultAvailability();
    }

    return {
      funnel: config.funnel ?? [],
      format: config.format ?? [],
      length: config.length ?? [],
      platform: config.platform ?? [],
      region: config.region ?? [],
      agency: config.agency ?? [],
      focus: config.focus ?? [],
      type: config.type ?? [],
      category: config.category ?? [],
    };
  }

  /**
   * Returns default availability object with all properties set to empty arrays
   *
   * @returns Default availability object with empty arrays
   */
  private getDefaultAvailability(): AssetPropertiesAvailability {
    return {
      funnel: [],
      format: [],
      length: [],
      platform: [],
      region: [],
      agency: [],
      focus: [],
      type: [],
      category: [],
    };
  }
}
